import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const OVERRIDE_PREFIX = 'fmo_override_';

export function getOverrideKey(lang: string, namespace: string, key: string): string {
  return `${OVERRIDE_PREFIX}${lang}_${namespace}_${key}`;
}

export function getContentOverride(lang: string, namespace: string, key: string): string | null {
  return localStorage.getItem(getOverrideKey(lang, namespace, key));
}

export function setContentOverride(lang: string, namespace: string, key: string, value: string): void {
  localStorage.setItem(getOverrideKey(lang, namespace, key), value);
}

export function removeContentOverride(lang: string, namespace: string, key: string): void {
  localStorage.removeItem(getOverrideKey(lang, namespace, key));
}

export function getAllOverridesForNamespace(lang: string, namespace: string): Record<string, string> {
  const overrides: Record<string, string> = {};
  const prefix = `${OVERRIDE_PREFIX}${lang}_${namespace}_`;
  for (let i = 0; i < localStorage.length; i++) {
    const storageKey = localStorage.key(i);
    if (storageKey && storageKey.startsWith(prefix)) {
      const contentKey = storageKey.slice(prefix.length);
      const value = localStorage.getItem(storageKey);
      if (value !== null) overrides[contentKey] = value;
    }
  }
  return overrides;
}

export function getAllOverridesForAllLanguages(): Record<string, Record<string, Record<string, string>>> {
  const result: Record<string, Record<string, Record<string, string>>> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const storageKey = localStorage.key(i);
    if (storageKey && storageKey.startsWith(OVERRIDE_PREFIX)) {
      const parts = storageKey.slice(OVERRIDE_PREFIX.length).split('_');
      if (parts.length >= 3) {
        const lang = parts[0];
        const namespace = parts[1];
        const key = parts.slice(2).join('_');
        const value = localStorage.getItem(storageKey);
        if (value !== null) {
          if (!result[lang]) result[lang] = {};
          if (!result[lang][namespace]) result[lang][namespace] = {};
          result[lang][namespace][key] = value;
        }
      }
    }
  }
  return result;
}

export function clearAllOverrides(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(OVERRIDE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
}

export function useContent(namespace: string) {
  const { t, i18n } = useTranslation(namespace);

  const contentT = useMemo(() => {
    return (key: string, options?: Record<string, unknown>): string => {
      const lang = i18n.language.startsWith('en') ? 'en' : 'es';
      const override = getContentOverride(lang, namespace, key);
      if (override !== null) {
        if (options && typeof override === 'string') {
          return override.replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(options[name] ?? ''));
        }
        return override;
      }
      return t(key, options as Record<string, string>);
    };
  }, [t, i18n.language, namespace]);

  return { t: contentT, i18n };
}